import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Palette, LogOut } from 'lucide-react';

import { useQueryClient } from '@tanstack/react-query';
import { useCompanyStore } from '../../store/company';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../providers/ThemeProvider';
import ThemeModal from '../common/ThemeModal';
import './Topbar.css';

const Topbar = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companyId, companies, switchCompany } = useCompanyStore();
  const { logout, user } = useAuth();
  const { theme, actualTheme, isDark } = useTheme();
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [switchingCompany, setSwitchingCompany] = useState(false);

  const currentCompany = companies.find(c => c.id === companyId);

  // Verificar se há dados válidos
  if (!companies || companies.length === 0) {
    return (
      <header className="topbar">
        <div className="topbar__left">
          <h1 className="topbar__title">RochaSystem</h1>
        </div>
        <div className="topbar__right">
          <span>Carregando empresas...</span>
        </div>
      </header>
    );
  }

  const handleCompanyChange = async newCompanyId => {
    const company = companies.find(c => c.id === newCompanyId);
    if (!company || newCompanyId === companyId) {
      setShowCompanyDropdown(false);
      return;
    }

    setSwitchingCompany(true);
    setShowCompanyDropdown(false);
    try {
      await queryClient.cancelQueries();
      queryClient.removeQueries({
        predicate: query => query.queryKey?.[0] !== 'companies',
      });
      switchCompany(newCompanyId);
      navigate('/rocha-wms/spaces');
    } finally {
      setSwitchingCompany(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const getInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getThemeLabel = () => {
    if (theme === 'system') return 'Sistema';
    return isDark ? 'Escuro' : 'Claro';
  };

  const getThemeIcon = () => {
    if (theme === 'system') return '🖥️';
    return isDark ? '🌙' : '☀️';
  };

  const brand = (() => {
    const logoUrl = currentCompany?.logo_url?.trim();
    const name = currentCompany?.name || 'RochaSystem';
    if (logoUrl) {
      return (
        <div className="topbar__brand" title={name}>
          <img src={logoUrl} alt={name} className="topbar__brand-logo" />
        </div>
      );
    }
    return (
      <h1 className="topbar__title" title={name}>
        {name}
      </h1>
    );
  })();

  return (
    <header className="topbar" aria-busy={switchingCompany}>
      <div className="topbar__left">
        <button className="topbar__menu-button" onClick={onMenuClick} aria-label="Abrir menu">
          ☰
        </button>

        {brand}
        {switchingCompany && <span role="status">Carregando dados da empresa...</span>}
      </div>

      <div className="topbar__right">
        {/* Seletor de Empresa */}
        <div className="topbar__company-selector">
          <button
            className="topbar__company-button"
            onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
          >
            <span className="topbar__company-icon">🏢</span>
            <span className="topbar__company-name">
              {currentCompany
                ? currentCompany.name || 'Empresa não encontrada'
                : 'Selecionar Empresa'}
            </span>
            <span className="topbar__company-arrow">▼</span>
          </button>

          {showCompanyDropdown && (
            <div className="topbar__company-dropdown">
              {companies.map(company => (
                <button
                  key={company.id}
                  className={`topbar__company-option ${
                    company.id === companyId ? 'topbar__company-option--active' : ''
                  }`}
                  onClick={() => handleCompanyChange(company.id)}
                >
                  {company.name}
                  {company.role && (
                    <span className="topbar__company-role">({company.role.name})</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Menu do Usuário */}
        {user && (
          <div className="topbar__user" style={{ position: 'relative' }}>
            <button
              className="topbar__avatar"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="Abrir menu do usuário"
              title={user.name}
            >
              {getInitials(user.name)}
            </button>

            {showUserMenu && (
              <div className="topbar__user-menu">
                <div className="topbar__user-header">
                  <div className="topbar__avatar topbar__avatar--sm">{getInitials(user.name)}</div>
                  <div className="topbar__user-meta">
                    <div className="topbar__user-name">{user.name}</div>
                    <div className="topbar__user-email">{user.email}</div>
                  </div>
                </div>

                <button
                  className="topbar__user-item"
                  onClick={() => (window.location.href = '/profile')}
                >
                  <User size={16} />
                  Perfil
                </button>

                <button
                  className="topbar__user-theme-section"
                  onClick={() => {
                    setShowThemeModal(true);
                    setShowUserMenu(false);
                  }}
                >
                  <Palette size={16} />
                  <div className="topbar__user-theme-content">
                    <div className="topbar__user-theme-label">Tema</div>
                    <div className="topbar__user-theme-subtitle">
                      {getThemeIcon()} {getThemeLabel()}
                    </div>
                  </div>
                </button>

                <hr className="topbar__divider" />

                <button
                  className="topbar__user-item topbar__user-item--danger"
                  onClick={handleLogout}
                >
                  <LogOut size={16} />
                  Sair
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Overlay para fechar dropdown */}
      {(showCompanyDropdown || showUserMenu) && (
        <div
          className="topbar__dropdown-overlay"
          onClick={() => {
            setShowCompanyDropdown(false);
            setShowUserMenu(false);
          }}
        />
      )}

      {/* Modal de Tema */}
      <ThemeModal isOpen={showThemeModal} onClose={() => setShowThemeModal(false)} />
    </header>
  );
};

export default Topbar;
